import { dataURItoBlob, getNameFromFileName } from '../../../util/UtilFunctions';
import * as pdfjs from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

type UseCase = 'profile' | 'workspace' | 'coverImage';

export const getHeadline = (useCase: UseCase): string => {
  switch (useCase) {
    case 'profile':
      return 'Add profile picture';
    case 'workspace':
      return 'Add workspace icon';
    case 'coverImage':
      return 'Add cover image';
    default:
      return '';
  }
};

const getPngFromPaintedCanvas = (file: File, canvasRef: HTMLCanvasElement): Promise<File> =>
  new Promise((resolve) => {
    setTimeout(() => {
      const canvasDataURl = canvasRef.toDataURL('image/png');
      const blob = dataURItoBlob(canvasDataURl);
      const fileNameBody = getNameFromFileName(file.name);
      const newFileName = `${fileNameBody}.png`;
      const newFile = new File([blob], newFileName);
      resolve(newFile);
    }, 100);
  });

const paintPageToCanvas = async (page: pdfjs.PDFPageProxy): Promise<HTMLCanvasElement> => {
  const viewport = page.getViewport({ scale: 1.0 });
  const canvasElement = document.createElement('canvas');
  const context = canvasElement.getContext('2d')!;
  canvasElement.width = viewport.width;
  canvasElement.height = viewport.height;
  const newPromise = new Promise<HTMLCanvasElement>((resolve) => {
    page
      .render({
        canvasContext: context,
        viewport,
      })
      .promise.then(() => resolve(canvasElement));
  });
  return newPromise;
};

const readPdf = (file: File): Promise<pdfjs.PDFDocumentProxy> => {
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    pdfjs
      .getDocument({ url })
      .promise.then((result) => resolve(result))
      .catch((readDocumentError) => reject(readDocumentError));
  });
};

const paintPdfFirstPageToCanvas = async (file: File): Promise<HTMLCanvasElement | undefined> => {
  try {
    const pdfFile = await readPdf(file);
    // if pdf is a massive one page picture, do not generate thumbnail for performance reasons
    const shouldNotGenerateThumb = pdfFile.numPages === 1 && file.size > 10000000;
    if (!shouldNotGenerateThumb) {
      try {
        const page = await pdfFile.getPage(1);
        try {
          const paintedCanvas = await paintPageToCanvas(page);
          return paintedCanvas;
        } catch {
          return undefined;
        }
      } catch {
        return undefined;
      }
    } else {
      return undefined;
    }
  } catch {
    return undefined;
  }
};

export const pdfToPng = async (file: File): Promise<File> =>
  new Promise((resolve, reject) => {
    paintPdfFirstPageToCanvas(file).then(async (paintedCanvas) => {
      if (paintedCanvas) {
        try {
          const pngPromise = getPngFromPaintedCanvas(file, paintedCanvas);
          const newFile = await pngPromise;

          resolve(newFile);
        } catch (timeOutError) {
          reject(timeOutError);
        }
      } else {
        reject();
      }
    });
  });
